import React, { useEffect, useRef, useState } from 'react';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Check, ChevronRight, Calendar, Euro, TrendingUp, Clock, Gift, StickyNote, BarChart3, User as UserIcon } from 'lucide-react';
import { fmtDateFR, fmtDateTimeFR, daysSince } from './utils';

export function InsightPanel({ conv, account, sales, onPatch, onPatchAccount }) {
  const [tab, setTab] = useState('insight');
  const approved = sales.filter(s => s.status === 'approved');
  const nonRejected = sales.filter(s => s.status !== 'rejected');
  const maxSaleObj = approved.length ? approved.slice().sort((a, b) => b.amount - a.amount)[0] : null;
  const lastSaleObj = nonRejected.length ? nonRejected.slice().sort((a, b) => new Date(b.date || b.created_at || 0) - new Date(a.date || a.created_at || 0))[0] : null;
  const fanDays = daysSince(conv.created_at);
  const lastMsgDays = daysSince(conv.last_activity);
  const fmtBday = (v) => { if (!v) return ''; const d = new Date(v); return isNaN(d) ? v : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }); };

  // Recompute total spent live from all non-rejected sales (covers pending + approved)
  const liveSpent = nonRejected.reduce((s, x) => s + (x.amount || 0), 0);
  const spent = Math.max(conv.spent || 0, liveSpent);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Active tab content fills available vertical space */}
      <div className="flex-1 min-h-0 flex flex-col">
        {tab === 'insight' && (
          <div className="flex-1 overflow-y-auto scrollbar-thin p-3 space-y-2.5">
            <EditableField testid="first-name-field" label="PRÉNOM" value={conv.first_name} placeholder="Cliquer pour ajouter un prénom" onSave={v => onPatch({ first_name: v })} icon={UserIcon} />
            <EditableField testid="birthday-field" label="ANNIVERSAIRE" value={conv.birthday} placeholder="Cliquer pour définir l'anniversaire" type="date" onSave={v => onPatch({ birthday: v })} icon={Calendar} format={fmtBday} chevron />

            <div className="grid grid-cols-2 gap-2">
              <Stat icon={Euro} color="text-emerald-400" label="TOTAL DÉPENSÉ" value={`${spent.toFixed(2)} €`} sub={nonRejected.length ? `${nonRejected.length} vente${nonRejected.length > 1 ? 's' : ''}` : 'Aucune vente'} testid="conv-spent" />
              <Stat icon={TrendingUp} color="text-indigo-400" label="VENTE MAX" value={`${(maxSaleObj?.amount || 0).toFixed(2)} €`} sub={maxSaleObj ? fmtDateFR(maxSaleObj.date) : 'Aucune vente'} testid="conv-max-sale" />
              <Stat icon={Calendar} color="text-violet-400" label="DERNIÈRE VENTE" value={`${(lastSaleObj?.amount || 0).toFixed(2)} €`} sub={lastSaleObj ? fmtDateTimeFR(lastSaleObj.date) : 'Aucune vente'} testid="conv-last-sale" />
              <Stat icon={Gift} color="text-amber-400" label="TOTAL DONS" value={`${(conv.dons || 0).toFixed(2)} €`} sub={(conv.dons || 0) > 0 ? 'Dons reçus' : 'Aucun don'} testid="conv-dons" />
              <Stat icon={Clock} color="text-orange-400" label="FAN DEPUIS" value={fanDays !== null ? `${fanDays} jour${fanDays > 1 ? 's' : ''}` : '—'} sub={conv.created_at ? `Depuis le ${fmtDateFR(conv.created_at)}` : ''} testid="conv-fan-since" />
              <Stat icon={Clock} color="text-rose-400" label="DERNIER MESSAGE" value={lastMsgDays !== null ? `${lastMsgDays} jour${lastMsgDays > 1 ? 's' : ''}` : '—'} sub={conv.last_activity ? fmtDateTimeFR(conv.last_activity) : ''} testid="conv-last-message" />
            </div>

            {/* 15/09 (Marlon) : fiche fan « L'essentiel » retirée, les notes automatiques la remplacent */}
            <div className="pt-1">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-1.5"><StickyNote size={12} /> Notes</div>
              <NotesEditor conv={conv} template={account?.notes_template} onSave={(notes) => onPatch({ notes })} />
            </div>
          </div>
        )}

        {tab === 'sales' && (
          <div className="flex-1 overflow-y-auto scrollbar-thin p-3 space-y-2 items-stretch">
            {sales.length === 0 ? (
              <div className="text-center text-xs text-slate-500 py-8">Aucune vente sur cette conversation pour l'instant.</div>
            ) : [...sales].sort((a, b) => new Date(b.date || b.created_at || 0) - new Date(a.date || a.created_at || 0)).map(s => (
              <div key={s.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid={`conv-sale-${s.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-bold text-emerald-400">{s.amount} €</span>
                  <Badge className={s.status === 'approved' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : s.status === 'pending' ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' : 'bg-rose-500/15 text-rose-300 border-rose-500/30'}>{s.status}</Badge>
                </div>
                <div className="text-[10px] text-slate-500">{s.link || s.type} · {s.chatter_name || 'Non attribué'} · {s.source}</div>
                {s.note && <div className="text-[10px] text-slate-400 mt-1 italic">&quot;{s.note}&quot;</div>}
              </div>
            ))}
          </div>
        )}

        {tab === 'note' && (
          <ModelNotesEditor account={account} onSave={(internal_bio) => onPatchAccount({ internal_bio })} />
        )}
      </div>

      {/* Tab switcher at the bottom */}
      <div className="border-t border-white/5 flex shrink-0">
        {[
          { k: 'insight', label: 'Insight', Icon: BarChart3 },
          { k: 'sales', label: 'Ventes', Icon: Euro },
          { k: 'note', label: 'Note', Icon: StickyNote },
        ].map(({ k, label, Icon }) => (
          <button key={k} data-testid={`conv-${k}-tab`} onClick={() => setTab(k)}
            className={`flex-1 py-2.5 text-sm flex items-center justify-center gap-1.5 transition-colors ${tab === k ? 'bg-white/5 text-indigo-300' : 'text-slate-400 hover:bg-white/[0.02]'}`}>
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, color, label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-2.5" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1"><Icon size={12} className={color} /> {label}</div>
      <div className="text-[15px] font-bold text-white leading-tight">{value}</div>
      {sub ? <div className="text-[10px] text-slate-500 mt-0.5 truncate">{sub}</div> : null}
    </div>
  );
}

function EditableField({ label, value, placeholder, onSave, type = 'text', testid, icon: Icon, format, chevron }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || '');
  useEffect(() => { setV(value || ''); }, [value]);
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">{label}</div>
      {editing ? (
        <div className="flex gap-1.5">
          <Input data-testid={`${testid}-input`} type={type} value={v} onChange={e => setV(e.target.value)} autoFocus className="bg-white/5 border-white/10 text-white h-11 text-sm" />
          <Button data-testid={`${testid}-save`} size="icon" className="h-11 w-11 bg-indigo-500 hover:bg-indigo-600 shrink-0" onClick={async () => { await onSave(v); setEditing(false); }}><Check size={14} /></Button>
        </div>
      ) : (
        <button data-testid={testid} onClick={() => setEditing(true)} className={`w-full rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] px-3 py-2 flex items-center gap-2 text-left transition-colors ${value ? 'text-white' : 'text-slate-600'}`}>
          {Icon && <Icon size={15} className={value ? 'text-sky-400' : 'text-slate-600'} />}
          <span className="text-[13px] font-medium flex-1 truncate">{value ? (format ? format(value) : value) : placeholder}</span>
          {chevron && <ChevronRight size={15} className="text-slate-500 shrink-0" />}
        </button>
      )}
    </div>
  );
}

// 15/09 (Marlon) : lignes utiles pour vendre surlignées — JAUNE doux = argent (disposition à payer, budget, jour de paye,
// a payé, dépense), ORANGE doux = objections / négociation / réaction au lien / classement 🟡. Notes auto reconnues par leur
// libellé (« 🤖 budget : »…), notes manuelles par mots-clés simples (casse et accents ignorés).
const sansAccents = (t) => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
export function couleurNote(ligne) {
  const t = sansAccents(ligne).trim();
  if (!t || /:$/.test(t)) return null;  // ligne vide ou modèle pas rempli (« - Jour de paie : »)
  if (t.includes('🤖 classement :')) return t.includes('🟡') ? 'orange' : t.includes('🟢') ? 'jaune' : null;
  if (/🤖 (objections|reaction au lien)\s*:/.test(t)) return 'orange';
  if (/🤖 (disposition a payer|budget|jour de paye)\s*:/.test(t)) return 'jaune';
  if (/negoci|objection|\bprix\b|trop cher/.test(t)) return 'orange';
  if (/\b(pay(e|es|ee|ees|er|ent)?|paie|paid|budget|depens\w*|salaire)\b/.test(t)) return 'jaune';
  return null;
}
const FOND_NOTE = { jaune: 'bg-yellow-300/15 text-yellow-100', orange: 'bg-orange-400/20 text-orange-100' };

function NotesEditor({ conv, template, onSave }) {
  const [notes, setNotes] = useState(conv.notes || template || '');
  const [edition, setEdition] = useState(false);
  const zoneRef = useRef(null);
  const [saveState, setSaveState] = useState('idle');
  const timer = useRef(null);
  const idleTimer = useRef(null);
  const mounted = useRef(true);
  const lastConvId = useRef(conv.id);

  // Clear pending timers on unmount — otherwise switching fan mid-typing leaks
  // timers and triggers setState on an unmounted component.
  useEffect(() => () => { mounted.current = false; clearTimeout(timer.current); clearTimeout(idleTimer.current); }, []);

  // Only reset the editor when switching conversation — never while the user is typing.
  useEffect(() => {
    if (lastConvId.current !== conv.id) {
      lastConvId.current = conv.id;
      setNotes(conv.notes || template || '');
      setSaveState('idle');
      setEdition(false);
    }
  }, [conv.id, conv.notes, template]);

  // Passage en édition (clic sur la vue) : curseur à la fin du texte.
  useEffect(() => {
    const z = zoneRef.current;
    if (edition && z && document.activeElement !== z) { z.focus(); const n = z.value.length; z.setSelectionRange(n, n); }
  }, [edition]);

  const onChange = (v) => {
    setNotes(v);
    setSaveState('saving');
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      await onSave(v);
      if (!mounted.current) return;
      setSaveState('saved');
      idleTimer.current = setTimeout(() => { if (mounted.current) setSaveState('idle'); }, 2000);
    }, 800);
  };

  return (
    <div>
      {edition || !notes.trim() ? (
        <textarea ref={zoneRef} data-testid="insight-notes-textarea" value={notes} onChange={e => onChange(e.target.value)}
          onFocus={() => setEdition(true)} onBlur={() => setEdition(false)}
          placeholder={template || "- Âge :\n- Job :\n- Hobbies :\n- Jour de paie :"}
          className="min-h-[240px] w-full rounded-xl border border-white/10 bg-white/[0.02] p-3 text-xs text-slate-200 placeholder:text-slate-600 outline-none focus:border-indigo-500/40 resize-none font-mono leading-relaxed" />
      ) : (
        <div data-testid="insight-notes-lecture" role="button" tabIndex={0} title="Clique pour modifier"
          onClick={() => setEdition(true)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setEdition(true); } }}
          className="min-h-[240px] w-full rounded-xl border border-white/10 bg-white/[0.02] p-3 text-xs text-slate-200 font-mono leading-relaxed cursor-text whitespace-pre-wrap break-words hover:border-white/20">
          {notes.split('\n').map((l, i) => {
            const c = couleurNote(l);
            return <div key={i} data-note-couleur={c || undefined} className={c ? `${FOND_NOTE[c]} rounded px-1 -mx-1` : ''}>{l || '\u00a0'}</div>;
          })}
        </div>
      )}
      {!edition && notes.trim() && (
        <div className="text-[9px] text-slate-600 mt-0.5" data-testid="insight-notes-legende">Clique pour modifier · <span className="text-yellow-200/80">jaune = argent</span> · <span className="text-orange-200/80">orange = objections</span></div>
      )}
      {saveState !== 'idle' && (
        <div className={`text-right text-[9px] mt-0.5 ${saveState === 'saved' ? 'text-emerald-400' : 'text-slate-600'}`}>
          {saveState === 'saving' ? 'Enregistrement…' : '✓ Enregistré'}
        </div>
      )}
    </div>
  );
}

function ModelNotesEditor({ account, onSave }) {
  const [notes, setNotes] = useState(account?.internal_bio || '');
  const [saveState, setSaveState] = useState('idle');
  const timer = useRef(null);
  const idleTimer = useRef(null);
  const mounted = useRef(true);
  const lastAccountId = useRef(account?.id);

  useEffect(() => () => { mounted.current = false; clearTimeout(timer.current); clearTimeout(idleTimer.current); }, []);

  // Only reset when switching account — never while the user is typing.
  useEffect(() => {
    if (lastAccountId.current !== account?.id) {
      lastAccountId.current = account?.id;
      setNotes(account?.internal_bio || '');
      setSaveState('idle');
    }
  }, [account?.id, account?.internal_bio]);

  const onChange = (v) => {
    setNotes(v);
    setSaveState('saving');
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      await onSave(v);
      if (!mounted.current) return;
      setSaveState('saved');
      idleTimer.current = setTimeout(() => { if (mounted.current) setSaveState('idle'); }, 2000);
    }, 800);
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col p-4 pb-3">
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center justify-between shrink-0">
        <span>Notes du modèle — {account?.name}</span>
        <span className={`normal-case font-normal ${saveState === 'saved' ? 'text-emerald-400' : 'text-slate-500'}`} data-testid="model-notes-save-state">
          {saveState === 'saving' ? 'Enregistrement…' : saveState === 'saved' ? '✓ Enregistré' : ''}
        </span>
      </div>
      <textarea data-testid="model-notes-textarea" value={notes} onChange={e => onChange(e.target.value)}
        placeholder="Notes sur ce compte modèle — persona, ton, ce qu'il faut dire / éviter… Visible par tous les employés."
        className="flex-1 min-h-[280px] w-full rounded-xl border border-white/10 bg-white/[0.02] p-3.5 text-[13px] text-slate-200 placeholder:text-slate-600 outline-none focus:border-indigo-500/40 resize-none leading-relaxed" />
      <p className="text-[10px] text-slate-600 mt-1.5 shrink-0">Notes au niveau du compte (identiques pour toutes les conversations de {account?.name}). Modifiables aussi dans Comptes → ✏️ Bio interne.</p>
    </div>
  );
}
